import { useEffect } from "react"
import { Outlet, useLocation } from "react-router-dom"
import { Provider } from "react-redux"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import store from "@/store"
import { hydrateCart } from "@/store/slices/cartSlice"
import { loadCart, saveCart } from "@/store/persist"
import { Toaster } from "@/components/ui/sonner"
import ErrorBoundary from "@/components/shared/ErrorBoundary"
import { initGtm, trackPageView } from "@/lib/analytics"
import UtmCapture from "@/features/utm/UtmCapture"
import { ActivePromotionProvider } from "@/features/promotions/ActivePromotionProvider"

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 60_000,
			refetchOnWindowFocus: false
		}
	}
})

/** Eventos que indican que la persona empezó a usar la página. */
const INTERACTION_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"] as const

/**
 * GTM se carga tras la primera interacción (o a los 5s si no hay ninguna), no en el
 * mount. Así el script y las cookies de terceros que dispara quedan fuera de la cadena
 * de dependencias de red de la carga inicial.
 */
function useDeferredGtm(): void {
	useEffect(() => {
		let started = false
		const start = (): void => {
			if (started) return
			started = true
			window.clearTimeout(fallback)
			INTERACTION_EVENTS.forEach(event => window.removeEventListener(event, start))
			initGtm()
		}
		const fallback = window.setTimeout(start, 5000)
		INTERACTION_EVENTS.forEach(event => window.addEventListener(event, start, { once: true, passive: true }))
		return () => {
			window.clearTimeout(fallback)
			INTERACTION_EVENTS.forEach(event => window.removeEventListener(event, start))
		}
	}, [])
}

function Analytics(): null {
	const location = useLocation()

	useDeferredGtm()

	useEffect(() => {
		trackPageView(location.pathname + location.search)
	}, [location.pathname, location.search])

	return null
}

/** Al cambiar de ruta, la nueva página se muestra desde arriba (sin conservar scroll). */
function ScrollToTop(): null {
	const { pathname } = useLocation()

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [pathname])

	return null
}

/**
 * Restaura el carrito persistido tras la hidratación (cliente) y lo guarda en
 * cada cambio del store. Hidratar en un efecto (no en el estado inicial) evita
 * desajustes de hidratación con el HTML pre-renderizado (SSG).
 *
 * Tras suscribirse, se hace un `saveCart` inmediato: en E2E (y en clientes
 * muy rápidos) los clics pueden ocurrir antes de que este efecto corra, y sin
 * ese flush el store en memoria no llegaría a localStorage.
 */
function useCartPersistence(): void {
	useEffect(() => {
		const persisted = loadCart()
		if (persisted.length > 0) {
			store.dispatch(hydrateCart(persisted))
		}
		const unsubscribe = store.subscribe(() => {
			saveCart(store.getState().cart.products)
		})
		saveCart(store.getState().cart.products)
		return unsubscribe
	}, [])
}

/**
 * Root element for every route: wires up Redux, React Query, toasts and
 * Google Analytics, then renders the matched route via <Outlet />.
 */
export default function AppRoot(): React.JSX.Element {
	useCartPersistence()

	return (
		<Provider store={store}>
			<QueryClientProvider client={queryClient}>
				<ActivePromotionProvider>
					<Analytics />
					<ScrollToTop />
					<UtmCapture />
					<ErrorBoundary>
						<Outlet />
					</ErrorBoundary>
					<Toaster richColors position="top-center" />
				</ActivePromotionProvider>
			</QueryClientProvider>
		</Provider>
	)
}
